import { useState } from 'react';
import { FaIdCard } from "react-icons/fa";
import MatchmakingResultCard from '../MatchmakingResultCard';
import MatchmakingResultCardSkeleton from '../MatchmakingResultCardSkeleton';
import { MatchmakingLoadingState } from './MatchmakingLoadingState';
import CareConciergContactForm from '@/features/provider-directory/components/CareConciergContactForm';
import type { MappedMatch } from '../../types/matchmaking.types';

type MatchmakingResultsSectionProps = {
  isLoading: boolean;
  hasMatches: boolean;
  showNoProvidersFound: boolean;
  isPreloadingImages: boolean;
  currentLoadingTheme: { text: string; color: string };
  currentLoadingMessageIndex: number;
  currentImagePreloadingTheme: { text: string; color: string };
  imagePreloadingMessageIndex: number;
  matchResults: MappedMatch[];
  preloadedImages: Record<string, string | null>;
  currentSearchId: number | null;
  advisorId: string | null;
  clientId: string | null;
  isViewingHistoryResults: boolean;
  onHistoryDetailsClick: () => void;
  cheerfulMessage: string;
  shouldShowPostSingleLoadExhaustedMessage: boolean;
  shouldShowPostSecondLoadMessage: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreMessage: string | null;
  onLoadMore: () => void;
  onContactUs: () => void;
};

export const MatchmakingResultsSection = ({
  isLoading,
  hasMatches,
  showNoProvidersFound,
  isPreloadingImages,
  currentLoadingTheme,
  currentLoadingMessageIndex,
  currentImagePreloadingTheme,
  imagePreloadingMessageIndex,
  matchResults,
  preloadedImages,
  currentSearchId,
  advisorId,
  clientId,
  isViewingHistoryResults,
  onHistoryDetailsClick,
  cheerfulMessage,
  shouldShowPostSingleLoadExhaustedMessage,
  shouldShowPostSecondLoadMessage,
  hasMore,
  isLoadingMore,
  loadMoreMessage: _loadMoreMessage,
  onLoadMore,
  onContactUs: _onContactUs,
}: MatchmakingResultsSectionProps) => {
  const [showHistoryDetailsTooltip, setShowHistoryDetailsTooltip] = useState(false);

  return (
    <section className="space-y-5">
      {shouldShowPostSingleLoadExhaustedMessage && (
        <div className="rounded-2xl border border-[#E4E5EA] bg-[#F9FAFB] px-4 py-3 font-termina text-sm text-[#6B7280] text-center">
          You've viewed all available results for this search.
        </div>
      )}

      {shouldShowPostSecondLoadMessage && (
        <div className="rounded-2xl border border-[#E4E5EA] bg-[#F9FAFB] px-4 py-3 font-termina text-sm text-[#6B7280] text-center">
          Continue searching for more matches below.
        </div>
      )}

      {!isLoading && hasMatches && !showNoProvidersFound && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 px-2">
            <div>
              <p className="font-terminaMedium text-xl text-[#111827]">
                {cheerfulMessage}
              </p>
            </div>
          </div>
          {isViewingHistoryResults && (
            <div className="relative">
              <button
                type="button"
                onClick={onHistoryDetailsClick}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-[#026857] text-white transition-colors hover:bg-[#025247] hover:shadow-lg"
                aria-label="View search details"
                onMouseEnter={() => setShowHistoryDetailsTooltip(true)}
                onMouseLeave={() => setShowHistoryDetailsTooltip(false)}
              >
                <FaIdCard className="w-5 h-5" />
              </button>
              {showHistoryDetailsTooltip && (
                <div className="absolute right-0 bottom-full mb-2 pointer-events-none z-50">
                  <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina text-[#111827]">
                    View search details
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <>
          <MatchmakingLoadingState
            currentLoadingTheme={currentLoadingTheme}
            currentLoadingMessageIndex={currentLoadingMessageIndex}
          />
          <div className="flex flex-col gap-5">
            {[1, 2, 3].map((i) => (
              <MatchmakingResultCardSkeleton key={`skeleton-${i}`} />
            ))}
          </div>
        </>
      )}

      {!isLoading && hasMatches && !showNoProvidersFound && (
        <>
          {isPreloadingImages && (
            <div className="space-y-8">
              <div className="flex items-center justify-center gap-4 py-8">
                <div className="relative flex-shrink-0">
                  <svg
                    className="animate-spin h-12 w-12"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    style={{ color: currentImagePreloadingTheme.color }}
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
                <div className="max-w-md">
                  <p 
                    key={imagePreloadingMessageIndex}
                    className="text-lg font-termina"
                    style={{
                      animation: 'fadeIn 0.5s ease-in-out',
                      color: currentImagePreloadingTheme.color
                    }}
                  >
                    {currentImagePreloadingTheme.text}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-5">
                {[1, 2, 3].map((i) => (
                  <MatchmakingResultCardSkeleton key={`skeleton-${i}`} />
                ))}
              </div>
            </div>
          )}

          {!isPreloadingImages && (
            <div className="flex flex-col gap-5">
              {matchResults.map((match) => (
                <MatchmakingResultCard
                  key={match.id}
                  name={match.name}
                  tags={match.tags}
                  serviceArea={match.serviceArea}
                  serviceType={match.serviceType}
                  aiRationale={match.aiRationale}
                  matchLevel={match.matchLevel}
                  matchLabel={match.matchLabel}
                  imageSrc={match.imageSrc}
                  providerProfile={match.providerProfile}
                  searchId={currentSearchId}
                  advisorId={advisorId ?? undefined}
                  clientId={clientId ?? undefined}
                  preloadedImageUrl={
                    (() => {
                      const providerId = match.providerProfile?.provider_id;
                      if (providerId && providerId in preloadedImages) {
                        return preloadedImages[providerId] || null;
                      }
                      return null;
                    })()
                  }
                />
              ))}
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center py-2">
              <button
                type="button"
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="flex items-center gap-2 rounded-full border border-[#026857] bg-white px-8 py-3 font-termina text-base font-medium text-[#026857] transition-colors hover:bg-[#026857] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoadingMore ? (
                  <>
                    <svg
                      className="h-5 w-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Searching for more matches...</span>
                  </>
                ) : (
                  <>
                    <span>Search More</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}

          {!isLoadingMore && !hasMore && hasMatches && (
            <div className="mt-8">
              <CareConciergContactForm
                sourceContext="navigator-exhausted"
                pageOrigin="provider-directory-no-results"
                className="max-w-4xl mx-auto"
              />
            </div>
          )}
        </>
      )}
    </section>
  );
};

